import * as React from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, HelpCircle, Loader2 } from 'lucide-react'
import { useForm, Controller, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import {
    usePaymentFlow,
    useUpdatePaymentFlow,
    PaymentFlowDetails,
} from '@/lib/api/paymentFlows'
import { ErrorDisplay } from '@/components/ui/error-display'
import { parseTRPCError } from '@/lib/utils/trpc-error-utils'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardAction, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { PAYMENT_FLOW_SUB_NAVIGATION_IDS, PaymentFlowSubNavigation } from '../components/sub-menu-navigation'
import { BackButton } from '@/components/ui/BackButton'
import { useOrgNavigate } from '@/hooks/use-app-navigate'

/**
 * Zod schema for abuse detection configuration
 */
const abuseDetectionConfigSchema = z.object({
    enabled: z.boolean(),
    timeWindowMinutes: z.number().min(1).max(120),
    detectIpAddress: z.boolean(),
    detectBin: z.boolean(),
    detectEmail: z.boolean(),
    ipAddressThreshold: z.number().min(1).max(50),
    binThreshold: z.number().min(1).max(100),
    emailThreshold: z.number().min(1).max(50),
    onlyFailedPayments: z.boolean(),
    includeDraftPayments: z.boolean(),
    autoBlockTtlHours: z.number().min(1).max(8760),
    autoBlockPermanent: z.boolean(),
})

const abuseDetectionFormSchema = z.object({
    abuseDetectionEnabled: z.boolean(),
    config: abuseDetectionConfigSchema.nullable(),
}).refine(
    (data) => {
        if (data.abuseDetectionEnabled) {
            return data.config !== null
        }
        return true
    },
    {
        message: 'Config is required when abuse detection is enabled',
        path: ['config'],
    }
)

export type AbuseDetectionFormData = z.infer<typeof abuseDetectionFormSchema>

/**
 * Helper component for labels with tooltips
 */
const LabelWithTooltip = ({ label, tooltip }: { label: string; tooltip: string }) => (
    <div className="flex items-center gap-1">
        <Label className="text-sm">{label}</Label>
        <Tooltip>
            <TooltipTrigger asChild>
                <HelpCircle className="size-3 cursor-help text-gray-400 hover:text-gray-600" />
            </TooltipTrigger>
            <TooltipContent>
                <p className="max-w-xs text-xs">{tooltip}</p>
            </TooltipContent>
        </Tooltip>
    </div>
)

/**
 * Loading skeleton for abuse detection page
 */
const AbuseDetectionSkeleton = () => (
    <div className="">
        <div className="mb-4">
            <Skeleton className="h-9 w-full max-w-md" />
        </div>
        <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
                <Skeleton className="size-10" />
                <div className="space-y-2">
                    <Skeleton className="h-8 w-64" />
                    <Skeleton className="h-4 w-96" />
                </div>
            </div>
        </div>
        <Skeleton className="h-[600px] w-full rounded-xl" />
    </div>
)

/**
 * Container component for abuse detection page
 */
const AbuseDetectionPageInner = () => {
    const { flowId } = useParams()
    const { t } = useTranslation('paymentFlows')

    const {
        data: paymentFlow,
        isLoading,
        error,
    } = usePaymentFlow(flowId || '', !!flowId)

    if (!flowId) {
        return null
    }

    if (isLoading) {
        return <AbuseDetectionSkeleton />
    }

    if (error) {
        return <ErrorDisplay error={parseTRPCError(error)} />
    }

    if (!paymentFlow) {
        return (
            <div className="container mx-auto py-8">
                <p>{t('paymentFlowNotFound')}</p>
            </div>
        )
    }

    return <AbuseDetectionContent paymentFlow={paymentFlow} />
}

/**
 * Default config values when enabling abuse detection
 */
const getDefaultConfig = () => ({
    enabled: true,
    timeWindowMinutes: 10,
    detectIpAddress: true,
    detectBin: true,
    detectEmail: true,
    ipAddressThreshold: 5,
    binThreshold: 8,
    emailThreshold: 8,
    onlyFailedPayments: true,
    includeDraftPayments: false,
    autoBlockTtlHours: 24,
    autoBlockPermanent: false,
})

/**
 * Form component for editing abuse detection settings
 */
const AbuseDetectionContent = ({
    paymentFlow,
}: {
    paymentFlow: NonNullable<PaymentFlowDetails>
}) => {
    const { orgRoutes, navigate } = useOrgNavigate()
    const { mutateAsync: updatePaymentFlow, isPending: isUpdatingPaymentFlow } = useUpdatePaymentFlow()
    const { t } = useTranslation(['paymentFlows', 'common'])
    const methods = useForm<AbuseDetectionFormData>({
        resolver: zodResolver(abuseDetectionFormSchema),
        defaultValues: {
            abuseDetectionEnabled: paymentFlow.abuseDetectionConfig?.enabled ?? false,
            config: paymentFlow.abuseDetectionConfig?.enabled ? paymentFlow.abuseDetectionConfig : null,
        },
    })
    const {
        handleSubmit,
        control,
        watch,
        setValue,
        formState: { errors },
    } = methods
    const abuseDetectionEnabled = watch('abuseDetectionEnabled')
    const config = watch('config')

    const onSubmit = (data: AbuseDetectionFormData) => {
        updatePaymentFlow({
            flowId: paymentFlow.id,
            data: {
                name: paymentFlow.name,
                pickProcessorStrategy: (paymentFlow.pickProcessorStrategy as 'weighted' | 'automatic') || 'weighted',
                fallbackMode: paymentFlow.fallbackMode ?? false,
                maxFallbackRetries: paymentFlow.maxFallbackRetries ?? 2,
                threeDsEnabled: paymentFlow.threeDsEnabled ?? false,
                stickyProcessorEnabled: paymentFlow.stickyProcessorEnabled ?? false,
                processorConfigs:
                    paymentFlow.processorConfigs?.map((config) => ({
                        processorId: config.processorId || '',
                        weight: config.weight,
                        disabled: config.disabled,
                        nonStickable: config.nonStickable,
                    })) || [],
                fallbackProcessorConfigs:
                    paymentFlow.fallbackProcessorConfigs?.map((config, index) => ({
                        processorId: config.processorId,
                        orderIndex: config.orderIndex,
                    })) || [],
                abuseDetectionConfig: data.abuseDetectionEnabled ? data.config : null,
            },
        }, {
            onSuccess: () => {
                navigate(orgRoutes.PAYMENT_FLOW_DETAILS(paymentFlow.id))
            },
        })
    }



    const onError = (errors: any) => {
        console.error('Form validation errors:', errors)
    }

    const handleCancel = () => {
        navigate(orgRoutes.PAYMENT_FLOW_DETAILS(paymentFlow.id))
    }

    const handleToggleAbuseDetection = (checked: boolean) => {
        setValue('abuseDetectionEnabled', checked)
        if (checked && !config) {
            setValue('config', getDefaultConfig())
        } else if (!checked) {
            setValue('config', null)
        }
    }

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit, onError)}>
                <PaymentFlowSubNavigation activeId={PAYMENT_FLOW_SUB_NAVIGATION_IDS.ABUSE_DETECTION} />
                {Object.keys(errors).length > 0 && (
                    <Alert variant="destructive" className="mb-4">
                        <AlertDescription>
                            {t('abuseDetection.validationErrors')}
                        </AlertDescription>
                    </Alert>
                )}
                <div className="flex items-center justify-between mb-8  ">
                    <div className="flex items-center gap-4">
                        <BackButton to={orgRoutes.PAYMENT_FLOW_DETAILS(paymentFlow.id)} />
                        <div>
                            <h1 className="text-2xl font-bold sm:text-3xl">
                                {t('abuseDetection.title')}
                            </h1>
                            <p className="text-muted-foreground text-sm">
                                {t('abuseDetection.subtitle', { name: paymentFlow.name })}
                            </p>
                        </div>
                    </div>
                    <div className="mt-6 flex justify-end gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleCancel}
                            disabled={isUpdatingPaymentFlow}
                        >
                            {t('common:cancel')}
                        </Button>
                        <Button type="submit" disabled={isUpdatingPaymentFlow}>
                            {isUpdatingPaymentFlow && <Loader2 className="size-4 animate-spin" />}
                            {t('common:save')}
                        </Button>
                    </div>
                </div>
                <Card>
                    <CardHeader>
                        <div className="flex items-center gap-2">
                            <CardTitle>{t('abuseDetection.card.title')}</CardTitle>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <HelpCircle className="size-4 cursor-help text-gray-400 hover:text-gray-600" />
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p className="max-w-sm text-xs">
                                        {t('abuseDetection.card.description')}
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </div>
                        <CardAction>
                            <Controller
                                name="abuseDetectionEnabled"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <Switch checked={value} onCheckedChange={(checked) => {
                                        onChange(checked)
                                        handleToggleAbuseDetection(checked)
                                    }} />
                                )}
                            />
                        </CardAction>
                    </CardHeader>

                    <CardContent>
                        <div className="mb-6">
                            <p className="text-sm text-gray-600">
                                {!abuseDetectionEnabled ? (
                                    <>
                                        <span className="font-medium text-gray-600">
                                            {t('abuseDetection.status.disabled.label')}
                                        </span> {t('abuseDetection.status.disabled.description')}
                                    </>
                                ) : (
                                    <>
                                        <span className="font-medium text-blue-600">
                                            {t('abuseDetection.status.enabled.label')}
                                        </span> {t('abuseDetection.status.enabled.description')}
                                    </>
                                )}
                            </p>
                        </div>

                        {abuseDetectionEnabled && config && (
                            <div className="space-y-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <LabelWithTooltip
                                            label={t('abuseDetection.fields.timeWindow.label')}
                                            tooltip={t('abuseDetection.fields.timeWindow.tooltip')}
                                        />
                                        <Controller
                                            name="config.timeWindowMinutes"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    type="number"
                                                    {...field}
                                                    value={field.value ?? 10}
                                                    onChange={(e) => {
                                                        const value = Math.max(1, Math.min(120, parseInt(e.target.value) || 10))
                                                        field.onChange(value)
                                                    }}
                                                    min={1}
                                                    max={120}
                                                    className="mt-1"
                                                />
                                            )}
                                        />
                                        <p className="mt-1 text-xs text-gray-500">
                                            {t('abuseDetection.fields.timeWindow.hint')}
                                        </p>
                                        {errors.config?.timeWindowMinutes && (
                                            <p className="text-sm text-red-500">{errors.config.timeWindowMinutes.message}</p>
                                        )}
                                    </div>

                                    <div>
                                        <div className="flex items-center justify-between">
                                            <LabelWithTooltip
                                                label={t('abuseDetection.fields.ipAddress.label')}
                                                tooltip={t('abuseDetection.fields.ipAddress.tooltip')}
                                            />
                                            <Controller
                                                name="config.detectIpAddress"
                                                control={control}
                                                render={({ field: { value, onChange } }) => (
                                                    <Switch checked={value} onCheckedChange={onChange} />
                                                )}
                                            />
                                        </div>
                                        <Controller
                                            name="config.ipAddressThreshold"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    type="number"
                                                    {...field}
                                                    value={field.value ?? 5}
                                                    onChange={(e) => {
                                                        const value = Math.max(1, Math.min(50, parseInt(e.target.value) || 5))
                                                        field.onChange(value)
                                                    }}
                                                    min={1}
                                                    max={50}
                                                    disabled={!config.detectIpAddress}
                                                    className="mt-1"
                                                    placeholder={t('abuseDetection.fields.ipAddress.placeholder')}
                                                />
                                            )}
                                        />
                                        <p className="mt-1 text-xs text-gray-500">
                                            {config.detectIpAddress
                                                ? t('abuseDetection.fields.ipAddress.hintEnabled')
                                                : t('abuseDetection.fields.ipAddress.hintDisabled')}
                                        </p>
                                        {errors.config?.ipAddressThreshold && (
                                            <p className="text-sm text-red-500">{errors.config.ipAddressThreshold.message}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <div className="flex items-center justify-between">
                                            <LabelWithTooltip
                                                label={t('abuseDetection.fields.bin.label')}
                                                tooltip={t('abuseDetection.fields.bin.tooltip')}
                                            />
                                            <Controller
                                                name="config.detectBin"
                                                control={control}
                                                render={({ field: { value, onChange } }) => (
                                                    <Switch checked={value} onCheckedChange={onChange} />
                                                )}
                                            />
                                        </div>
                                        <Controller
                                            name="config.binThreshold"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    type="number"
                                                    {...field}
                                                    value={field.value ?? 8}
                                                    onChange={(e) => {
                                                        const value = Math.max(1, Math.min(100, parseInt(e.target.value) || 8))
                                                        field.onChange(value)
                                                    }}
                                                    min={1}
                                                    max={100}
                                                    disabled={!config.detectBin}
                                                    className="mt-1"
                                                    placeholder={t('abuseDetection.fields.bin.placeholder')}
                                                />
                                            )}
                                        />
                                        <p className="mt-1 text-xs text-gray-500">
                                            {config.detectBin
                                                ? t('abuseDetection.fields.bin.hintEnabled')
                                                : t('abuseDetection.fields.bin.hintDisabled')}
                                        </p>
                                        {errors.config?.binThreshold && (
                                            <p className="text-sm text-red-500">{errors.config.binThreshold.message}</p>
                                        )}
                                    </div>

                                    <div>
                                        <div className="flex items-center justify-between">
                                            <LabelWithTooltip
                                                label={t('abuseDetection.fields.email.label')}
                                                tooltip={t('abuseDetection.fields.email.tooltip')}
                                            />
                                            <Controller
                                                name="config.detectEmail"
                                                control={control}
                                                render={({ field: { value, onChange } }) => (
                                                    <Switch checked={value} onCheckedChange={onChange} />
                                                )}
                                            />
                                        </div>
                                        <Controller
                                            name="config.emailThreshold"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    type="number"
                                                    {...field}
                                                    value={field.value ?? 8}
                                                    onChange={(e) => {
                                                        const value = Math.max(1, Math.min(50, parseInt(e.target.value) || 8))
                                                        field.onChange(value)
                                                    }}
                                                    min={1}
                                                    max={50}
                                                    disabled={!config.detectEmail}
                                                    className="mt-1"
                                                    placeholder={t('abuseDetection.fields.email.placeholder')}
                                                />
                                            )}
                                        />
                                        <p className="mt-1 text-xs text-gray-500">
                                            {config.detectEmail
                                                ? t('abuseDetection.fields.email.hintEnabled')
                                                : t('abuseDetection.fields.email.hintDisabled')}
                                        </p>
                                        {errors.config?.emailThreshold && (
                                            <p className="text-sm text-red-500">{errors.config.emailThreshold.message}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <div>
                                        <div className="mb-3 flex items-center justify-between">
                                            <LabelWithTooltip
                                                label={t('abuseDetection.fields.permanent.label')}
                                                tooltip={t('abuseDetection.fields.permanent.tooltip')}
                                            />
                                            <Controller
                                                name="config.autoBlockPermanent"
                                                control={control}
                                                render={({ field: { value, onChange } }) => (
                                                    <Switch checked={value} onCheckedChange={onChange} />
                                                )}
                                            />
                                        </div>

                                        {!config.autoBlockPermanent && (
                                            <div>
                                                <LabelWithTooltip
                                                    label={t('abuseDetection.fields.ttl.label')}
                                                    tooltip={t('abuseDetection.fields.ttl.tooltip')}
                                                />
                                                <Controller
                                                    name="config.autoBlockTtlHours"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="number"
                                                            {...field}
                                                            value={field.value ?? 24}
                                                            onChange={(e) => {
                                                                const value = Math.max(1, Math.min(8760, parseInt(e.target.value) || 24))
                                                                field.onChange(value)
                                                            }}
                                                            min={1}
                                                            max={8760}
                                                            disabled={config.autoBlockPermanent}
                                                            className="mt-1"
                                                            placeholder={t('abuseDetection.fields.ttl.placeholder')}
                                                        />
                                                    )}
                                                />
                                                <div className="mt-2 flex flex-wrap gap-2">
                                                    <Button
                                                        type="button"
                                                        variant="secondary"
                                                        size="sm"
                                                        disabled={config.autoBlockPermanent}
                                                        onClick={() => setValue('config.autoBlockTtlHours', 1)}
                                                    >
                                                        {t('abuseDetection.fields.ttl.presets.oneHour')}
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="secondary"
                                                        size="sm"
                                                        disabled={config.autoBlockPermanent}
                                                        onClick={() => setValue('config.autoBlockTtlHours', 24)}
                                                    >
                                                        {t('abuseDetection.fields.ttl.presets.oneDay')}
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="secondary"
                                                        size="sm"
                                                        disabled={config.autoBlockPermanent}
                                                        onClick={() => setValue('config.autoBlockTtlHours', 168)}
                                                    >
                                                        {t('abuseDetection.fields.ttl.presets.oneWeek')}
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="secondary"
                                                        size="sm"
                                                        disabled={config.autoBlockPermanent}
                                                        onClick={() => setValue('config.autoBlockTtlHours', 720)}
                                                    >
                                                        {t('abuseDetection.fields.ttl.presets.oneMonth')}
                                                    </Button>
                                                </div>
                                                <p className="mt-1 text-xs text-gray-500">
                                                    {t('abuseDetection.fields.ttl.hint')}
                                                </p>
                                                {errors.config?.autoBlockTtlHours && (
                                                    <p className="text-sm text-red-500">{errors.config.autoBlockTtlHours.message}</p>
                                                )}
                                            </div>
                                        )}

                                        {config.autoBlockPermanent && (
                                            <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
                                                <div className="flex items-center gap-2">
                                                    <div className="size-2 rounded-full bg-blue-500" />
                                                    <p className="text-sm font-medium text-blue-800">
                                                        {t('abuseDetection.fields.permanent.enabledTitle')}
                                                    </p>
                                                </div>
                                                <p className="mt-1 text-xs text-blue-600">
                                                    {t('abuseDetection.fields.permanent.enabledDescription')}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {!abuseDetectionEnabled && (
                            <p className="text-sm text-gray-500">
                                {t('abuseDetection.toggleHint')}
                            </p>
                        )}
                    </CardContent>
                </Card>


            </form>
        </FormProvider>
    )
}

export const AbuseDetectionPage = withRequiredPermission(AbuseDetectionPageInner, PERMISSIONS.PROCESSORS_MANAGE)
export default AbuseDetectionPage

